export enum Permissions {
  ViewInvoices = "ViewInvoices",
  ModifyMenu = "ModifyMenu",
  /** Samo paljenje/gašenje artikala i grupa u lokalu; `ModifyMenu` ga podrazumeva. */
  ModifyMenuItemAvailability = "ModifyMenuItemAvailability",
  ModifyOrder = "ModifyOrder",
  ViewOrders = "ViewOrders",
  ChangeEmployees = "ChangeEmployees",
  ModifyCompany = "ModifyCompany",
  ModifyQRCode = "ModifyQRCode",
  GenerateSignInToken = "GenerateSignInToken",
  ViewEvents = "ViewEvents",
  ModifyPrices = "ModifyPrices",
  ModifyCustomers = "ModifyCustomers",
  CreateSupplyOrder = "CreateSupplyOrder",
  ViewDocuments = "ViewDocuments",
  ModifyCoupons = "ModifyCoupons",
  ViewCoupons = "ViewCoupons",
  ModifyLoyaltyPages = "ModifyLoyaltyPages",
  ViewUsers = "ViewUsers",
  AssignLoyaltyCoupons = "AssignLoyaltyCoupons",
  ModifyOrganization = "ModifyOrganization",
  SendPushMessages = "SendPushMessages",
  RefundPayment = "RefundPayment",
  FullAccess = "FullAccess",
}

export enum AdminPermissions {
  SetAdmin = "SetAdmin",
  ChangeAppSettings = "ChangeAppSettings",
  SetInvoicePaid = "SetInvoicePaid",
  ImportData = "ImportData",
  ImpersonateUser = "ImpersonateUser",
  ViewCompanies = "ViewCompanies",
  ModifyDefaultProducts = "ModifyDefaultProducts",
  ChangePermissions = "ChangePermissions",
  ViewLog = "ViewLog",
  ViewBilling = "ViewBilling",
  ManageBilling = "ManageBilling",
  ManageServiceMessages = "ManageServiceMessages",
}

export const AllPermissions = { ...Permissions, ...AdminPermissions };
export type AllPermissions = typeof AllPermissions;

/**
 * Nivo na kom dodela prava ima efekta. Ogledalo serverskog `PermissionScopes` — vrednosti se
 * poklapaju sa imenima zastavica, jer ih server serijalizuje kao string.
 *
 * Provera prava na serveru kaskadira samo NAVIŠE (lokal → organizacija → admin), nikad naniže:
 * pravo koje radi samo na org nivou (npr. loyalty) dodeljeno lokalu ne bi radilo ništa.
 */
export enum PermissionScopes {
  Company = 'Company',
  Organization = 'Organization',
  Admin = 'Admin',
}

/**
 * Jedan red serverskog kataloga prava (`role/PermissionCatalog`). Katalog je autoritativan izvor
 * za to šta se uopšte nudi u editoru — enumi iznad su samo za tipizovane provere u kodu i lako
 * zaostanu za serverom.
 */
export interface PermissionCatalogItem {
  code: Permissions;
  scopes: PermissionScopes[];
}

export interface UserPermissions {
  companyPermissions: CompanyPermissions[];
  adminPermissions: Permissions[];
}

export interface CompanyPermissions {
  companyID: number;
  permissions: Permissions[];

  /**
   * Rola dodeljena u ovom lokalu. Stiže samo sa putanje za UREĐIVANJE prava
   * (`user/getCompanyPermissions`), gde `permissions` nosi isključivo pojedinačno dodeljena prava.
   */
  roleID?: number | null;

  /**
   * Prava koja korisnik ima kroz rolu. Odvojena od `permissions` jer snimanje zamenjuje ceo skup —
   * pomešana bi se pri prvom snimanju prepisala u pojedinačna i rola bi se izgubila.
   */
  rolePermissions?: Permissions[];
}

/**
 * Prava prijavljenog korisnika nad JEDNIM lokalom, po oba nivoa odjednom
 * (`permissions/getMyPermissions?companyID=`).
 *
 * Dva niza, a ne jedan: server te dve provere ne izjednačava — pravo nad lokalom NE važi na org
 * nivou, dok obrnuto važi. Zajednički meni firme, na primer, sme da menja samo onaj ko ima
 * `ModifyMenu` nad ORGANIZACIJOM, pa klijent mora da zna iz kog nivoa pravo dolazi.
 *
 * Stižu zajedno jer su oba već u istom keširanom objektu na serveru — odvojeni pozivi ne bi ništa
 * uštedeli, a mogli bi da se raziđu i opišu dva različita lokala pri brzom prebacivanju.
 */
export interface EffectivePermissions {
  company: Permissions[];
  /** Prazno kad lokal nije u organizaciji; vlasnik organizacije dobija `FullAccess`. */
  organization: Permissions[];
}

export interface OrganizationPermissions {
  organizationID: number;
  permissions: Permissions[];

  /** Rola koja važi u svim lokalima organizacije; v. `CompanyPermissions.roleID`. */
  roleID?: number | null;

  rolePermissions?: Permissions[];
}
